import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { UtilsService, PopUpService } from 'core/services';
import { KeplerService } from 'app/kepler/services/kepler.service';
import { ActivatedRoute, Router } from '@angular/router';
import { KeplerMatch } from '../../kepler/models/KeplerMatch';
import { Client } from 'core/models/client';
import { Crumb } from '@models/Crumb';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lms-client-kepler-match',
  templateUrl: './client-kepler-match.component.html',
  styleUrls: ['./client-kepler-match.component.scss']
})
export class ClientKeplerMatchComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabGroup', { static: false }) public tabGroup: MatTabGroup;
  public tabsManager: any | Subscription = new Subscription();
  public crumbList: Crumb[];
  public client: Client = null;
  public match: KeplerMatch = null;
  public filters: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private popupService: PopUpService,
    private keplerService: KeplerService,
    private location: Location
  ) {}

  public ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.client = data['client'];
      this.match = data['keplerMatch'];
      this.crumbList = [
        { label: this.translateService.instant('pageTitle.clients'), icon: 'glicon-clients', route: '/clients' },
        { label: this.client.name, route: '/clients/' + this.client.id },
        { label: 'Kepler', route: '/clients/' + this.client.id, fragment: 'kepler' },
        {
          label: this.match.name,
          route: '/clients/' + this.client.id + '/kepler/' + this.match.id
        }
      ];
      this.filters = { matchId: this.match.id };
    });
  }

  public ngAfterViewInit(): void {
    this.tabsManager = UtilsService.createTabsManager(this.route, this.tabGroup);
  }

  public setTab(selectedTab: MatTabChangeEvent) {
    this.tabsManager.setTab(selectedTab);
  }

  public ngOnDestroy(): void {
    this.tabsManager.unsubscribe();
  }

  public updateMatch(match): void {
    if (!this.match) {
      this.goKeplerList();
    } else {
      this.match = { ...this.match, ...match };
    }
  }

  public goKeplerList(): void {
    this.router.navigate(['/clients', this.client.id, 'kepler', this.match.id], { replaceUrl: true }).then();
  }

  deleteMatch() {
    this.popupService
      .confirmDelete({
        title: this.translateService.instant('kepler.deleteMatchConfirmation', {
          match: this.match.name
        }),
        text: this.translateService.instant('kepler.deleteMatchMessage')
      })
      .then(() => this.keplerService.delete(this.match.id))
      .then(() => this.location.back())
      .catch((error) => {
        if (error !== 'cancel') {
          const errorLabel = `error.${error.errorCode}`;
          const translatedErrorMessage = this.translateService.instant(errorLabel);

          this.popupService.showError({
            title: this.translateService.instant('kepler.deleteMatchError'),
            text: translatedErrorMessage === errorLabel ? error.serverMessage : translatedErrorMessage
          });
        }
      });
  }

  getObjectivesDeadLineDays(): number {
    const startDate = new Date().getTime();
    const endDate = new Date(this.match.currentObjectivesDeadline).getTime();
    const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
    return days;
  }

  public downloadSupervisorsReport() {
    this.keplerService.downloadMatchSupervisorsReport(this.match.id).subscribe({
      next: (response) => {
        const contentDisposition = response.headers.get('content-disposition') || '';
        const matches = /filename=([^;]+)/gi.exec(contentDisposition);
        const fileName = (matches?.length ? matches[1].slice(1, -1) : `KeplerSupervisors_${this.match.id}.csv`).trim();
        saveAs(response.body, fileName);
      },
      error: (error) => {
        this.popupService.showError({
          title: 'Error',
          text: error.serverMessage
        });
      }
    });
  }
}
